import {
  Button,
  CloseIcon,
  FolderAddAltIcon,
  Header,
  IconButton,
  Paragraph,
  ResponsiveButton,
  TextInput,
  ToggleInput,
  notification,
} from "@datagame/ui";
import { ChangeEvent, useState } from "react";
import { Dialog } from "@headlessui/react";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";

import { Section } from "../../../project/ProjectView/ProjectSettings/Steps/Common";
import { Folder, FoldersList } from "../../../project/ProjectsView/FoldersSection/FoldersList";
import { DIRECTORY_CREATE } from "../../../../graphql/mutations/FolderMutations";
import { DIRECTORY_LIST } from "../../../../graphql/queries/ProjectQueries";
import { FolderChip } from "../../../project/ProjectsView/FoldersSection/FoldersList/FolderChip";

import styles from "./ManageFoldersModal.module.scss";

interface ManageFoldersModalProps {
  onClose: () => void;
  folders?: Folder[];
  selectedFolder?: Folder;
  setSelectedFolder?: (folder: Folder) => void;
  canManage?: boolean;
  title?: string;
  description?: string;
  confirmText?: string;
  confirmAction?: (id?: string) => void;
}

export const ManageFoldersModal = ({
  onClose,
  folders,
  selectedFolder,
  setSelectedFolder,
  canManage = true,
  title,
  description,
  confirmText,
  confirmAction,
}: ManageFoldersModalProps) => {
  const { t } = useTranslation();
  const [folderName, setFolderName] = useState("");
  const [nestFolder, setNestFolder] = useState(false);
  const [selectedParent, setSelectedParent] = useState("");
  const [createDirectory] = useMutation(DIRECTORY_CREATE, {
    refetchQueries: [{ query: DIRECTORY_LIST }],
  });

  const handleFolderNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFolderName(e.target.value);
  };

  const handleNestFolder = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.checked;
    setNestFolder(value);

    if (!value) {
      setSelectedParent("");
    }
  };

  const handleAddFolder = () => {
    if (!folderName) {
      notification.danger({
        title: t("folders:emptyNameTitle"),
        description: t("folders:emptyNameDescription"),
      });
      return;
    }

    const data = selectedParent
      ? {
          name: folderName,
          parentDirectoryId: selectedParent,
        }
      : {
          name: folderName,
        };

    createDirectory({
      variables: {
        data,
      },
    });

    setFolderName("");
    setNestFolder(false);
    setSelectedParent("");
  };

  return (
    <Dialog open={true} onClose={onClose}>
      <div className={styles.overlay} />
      <Dialog.Panel>
        <div className={styles.modal}>
          <div className={styles.headerWrapper}>
            <Header className={styles.header} variant="h6">
              {title ?? t("folders:manageFolders")}
            </Header>
            <IconButton size="small" onClick={onClose} className={styles.close} icon={<CloseIcon />} />
          </div>
          <Paragraph>{description ?? t("folders:description")}</Paragraph>

          {canManage && (
            <div className={styles.content}>
              <div className={styles.addFolderWrapper}>
                <TextInput
                  size="small"
                  placeholder={t("folders:addPlaceholder") ?? ""}
                  icon={<FolderAddAltIcon />}
                  value={folderName}
                  onChange={handleFolderNameChange}
                />
                <ResponsiveButton
                  kind="outline"
                  icon={<FolderAddAltIcon />}
                  iconSide="left"
                  size="small"
                  className={styles.addButton}
                  onClick={handleAddFolder}
                >
                  {t("folders:addFolder")}
                </ResponsiveButton>
              </div>
              <ToggleInput
                label={t("folders:nestInside") ?? ""}
                name="nest-inside"
                value={nestFolder}
                onChange={handleNestFolder}
              />

              {nestFolder && (
                <div className={styles.foldersChips}>
                  {folders?.map((folder) => {
                    return (
                      <FolderChip
                        key={folder.id}
                        folder={folder}
                        onSelect={setSelectedParent}
                        selectedParentId={selectedParent}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <Section headerClassName={styles.sectionHeader} title={t("folders:title")} className={styles.foldersSection}>
            <FoldersList
              groupedFolders={folders}
              isEditable={canManage}
              className={styles.foldersList}
              selected={selectedFolder}
              setSelected={setSelectedFolder}
            />
          </Section>

          {confirmText && (
            <Button
              className={styles.confirmButton}
              disabled={!selectedFolder}
              onClick={() => confirmAction?.(selectedFolder?.id)}
            >
              {confirmText}
            </Button>
          )}
        </div>
      </Dialog.Panel>
    </Dialog>
  );
};
